import React, { useCallback, useEffect, useMemo, useRef } from 'react';

import debounce from 'lodash/debounce';
import { useIntl } from 'react-intl';

import type { DocNode } from '@atlaskit/adf-schema/doc';
import { EVENT_TYPE } from '@atlaskit/editor-common/analytics';
import { useSharedPluginStateWithSelector } from '@atlaskit/editor-common/hooks';
import { findOverflowScrollParent } from '@atlaskit/editor-common/ui';
import type { JSONNode } from '@atlaskit/editor-json-transformer/types';
import FeatureGates from '@atlaskit/feature-gate-js-client/feature-gates';
import { FlagsProvider } from '@atlaskit/flag/flags-provider';
import { isExperimentEnabled } from '@atlaskit/platform-feature-experiments/is-experiment-enabled';
import { fg } from '@atlaskit/platform-feature-flags/fg';
import { expValEquals } from '@atlaskit/tmp-editor-statsig/exp-val-equals';
import { expValEqualsNoExposure } from '@atlaskit/tmp-editor-statsig/exp-val-equals-no-exposure';
import type { PromptContentChangePayload } from '@atlassian/conversation-assistant-chat-prompt-input/prompt-content-change-payload';
import { RovoInlineChatPrompt } from '@atlassian/conversation-assistant-chat-prompt-input/ui/inline-chat-prompt';
import {
	ExperienceName,
	remixEditExistingExperience,
	REMIX_EDIT_EXISTING_MARKS,
} from '@atlassian/conversation-assistant-instrumentation/constants/experience-tracker';
import { getRemixFeatureArea } from '@atlassian/conversation-assistant-instrumentation/controllers/experience-tracker/remix-feature-area';
import { Breakpoint } from '@atlassian/generative-ai-modal/hooks/useElementBreakpoints';
import { ROVO_CHAT_CLIENT_ID } from '@atlassian/rovo-content-bridge-api-commands/clients/rovo-chat-client';
import { ROVO_EXPERIENCE_TRACKING_SENDER_CLIENT_ID } from '@atlassian/rovo-content-bridge-api-commands/clients/rovo-experience-tracking-sender-client';
import { AifcExperienceTrackingCommand } from '@atlassian/rovo-content-bridge-api-commands/commands/aifc-preview-experience-tracking-command';
import { getBrowserBridgeAPI } from '@atlassian/rovo-content-bridge-api/singleton';

import { getAIExperienceContextPluginState } from '../../../../pm-plugins/ai-session-context/ai-session-context-plugin';
import { resetSessionState } from '../../../../pm-plugins/ai-session-context/commands';
import {
	createCommitInlineRovoStreamingCommand,
	createEndInlineRovoStreamingCommand,
	createShowInlineEditReadyCommand,
} from '../../../../pm-plugins/decoration/actions';
import { aiExperienceDecorationPluginKey } from '../../../../pm-plugins/decoration/decoration-plugin-key';
import type { EditorPluginAIProvider } from '../../../../types/types';
import { useContentComponentContext } from '../../../hooks/useContentComponentContext';
import { CONFIG_ITEM_KEYS } from '../../../prebuilt/config-items/config-item-keys';
import type { UserFlowAEP } from '../../../utils/analytics/analyticsFlowTypes';
import {
	isCollapseLoadingBarEnabled,
	isCollapseLoadingBarEnabledNoExposure,
} from '../../../utils/collapse-loading-bar-enabled';
import { isStreamingUxExperienceM1EnabledNoExposure } from '../../../utils/streaming-ux-experience-m1-enabled';

import { messages } from './messages';

export const providerProductToProductKey = (product: EditorPluginAIProvider['product']): string => {
	switch (product) {
		case 'CONFLUENCE':
			return 'confluence';
		case 'JSM':
			return 'jsm';
		case 'JPD':
			return 'jpd';
		case 'JSW':
		case 'JWM':
			return 'jira';
		case 'ATLAS':
		case 'TOWNSQUARE':
			return 'atlas';
		case 'BITBUCKET':
			return 'bitbucket';
		default:
			return 'unknown';
	}
};

const backupChannelVariables = {
	cloudId: '',
	orgId: undefined,
	userId: undefined,
};

const isMauiAppNode = (node?: JSONNode) =>
	node?.type === 'extension' &&
	node?.attrs != null &&
	'extensionKey' in node.attrs &&
	node.attrs.extensionKey === 'native-embed:maui';

const isMediaNode = (node?: JSONNode) => {
	if (!node) {
		return false;
	}

	if (node.type === 'media') {
		return true;
	}

	if (node.type === 'mediaSingle' && Array.isArray(node.content) && node.content.length > 0) {
		return (node.content[0] as JSONNode | undefined)?.type === 'media';
	}

	return false;
};

const getSelectionLabsLozengeVariant = (
	selectionFragment: JSONNode[] | null,
): 'default' | 'maui' | undefined => {
	if (!Array.isArray(selectionFragment) || selectionFragment.length === 0) {
		return undefined;
	}

	if (
		selectionFragment.some((node) => isMauiAppNode(node)) ||
		selectionFragment.some((node) => isMediaNode(node))
	) {
		return 'maui';
	}

	return undefined;
};

interface RovoPromptScreenWithLogicProps {
	autoClose?: boolean;
	cancel: (options?: { shouldClearSelectionContext?: boolean }) => void;
	/**
	 * Marks this prompt as the Post Stream Review (PSR) surface. Drives
	 * surface-specific behaviour — the PSR-specific placeholder and hiding the
	 * LABS lozenge — independently of the visual `rovoInlineChatAppearance`, so
	 * that another surface adopting `frameless` does not silently inherit PSR
	 * behaviour.
	 */
	isPostStreamReview?: boolean;
	/**
	 * Called whenever the prompt content changes, with the latest emptiness /
	 * length payload. Lets a parent surface (e.g. Post Stream Review) track
	 * whether the user has typed anything without reaching into the editor DOM.
	 * Fires independently of any analytics wiring.
	 */
	onPromptContentChange?: (payload: PromptContentChangePayload) => void;
	/**
	 * When provided, intercepts the prompt submit to open the Rovo chat sidebar
	 * instead of performing an AI inline edit. The callback receives the prompt
	 * DocNode and should return `true` to indicate it handled the submit.
	 */
	openInRovoChatConfig?: {
		onBeforeSubmit: (prompt: DocNode) => boolean;
	};
	/**
	 * Controls the outer-surface chrome of the embedded inline chat prompt.
	 * Forwarded directly to `RovoInlineChatPrompt`. Use `frameless` when the
	 * prompt is embedded inside another container (e.g. the Post Stream Review
	 * toolbar) that supplies its own padding, border, background and controls.
	 */
	rovoInlineChatAppearance?: 'default' | 'frameless';
	/**
	 * Forwarded to `RovoInlineChatPrompt`: re-shows the prompt's own add + submit
	 * controls while keeping the `frameless` surface. Post Stream Review sets this
	 * once the user starts composing a re-prompt so those controls appear in place
	 * of the hidden review controls.
	 */
	showTrailingActions?: boolean;
}

// eslint-disable-next-line jsdoc/require-jsdoc
export function RovoPromptScreenWithLogic({
	autoClose,
	cancel,
	isPostStreamReview = false,
	onPromptContentChange: onPromptContentChangeProp,
	openInRovoChatConfig,
	rovoInlineChatAppearance,
	showTrailingActions,
}: RovoPromptScreenWithLogicProps): React.JSX.Element | null {
	const { formatMessage } = useIntl();
	const {
		setIsRovoLoading,
		api,
		targetId,
		userADFInput,
		editorPluginAIProvider,
		appearance,
		setCancelRovoPrompt,
		popupsMountPoint,
		editorView,
	} = useContentComponentContext();

	// Placeholder override (CCI-17295 / CCI-17296). ai-mate stays gate-agnostic —
	// the editor decides whether to supply an override and ai-mate simply prefers
	// it when present, so non-AIFC consumers keep the default placeholder.
	//
	// The Post Stream Review prompt needs its own placeholder ("Describe a change
	// to keep editing"), which takes precedence over the generic
	// AIFC placeholder. PSR identity is signalled explicitly via the
	// `isPostStreamReview` prop rather than inferred from the visual appearance.
	const aifcPlaceholderText = isPostStreamReview
		? formatMessage(messages.postStreamReviewPromptPlaceholder)
		: isStreamingUxExperienceM1EnabledNoExposure()
			? formatMessage(messages.aifcPromptPlaceholder)
			: undefined;

	const isLoadingBarCollapseEnabled = isCollapseLoadingBarEnabledNoExposure();
	const thinkingBarScrollContainer = useMemo(() => {
		if (!isLoadingBarCollapseEnabled || isPostStreamReview || !editorView) {
			return undefined;
		}

		return (
			findOverflowScrollParent(editorView.dom) ||
			(typeof window !== 'undefined' ? window : undefined)
		);
	}, [editorView, isLoadingBarCollapseEnabled, isPostStreamReview]);

	// Agent selector placement (CCI-17299). Same gate-agnostic split as the
	// placeholder above: the editor owns the rollout decision and ai-mate just
	// honours the prop, so non-AIFC consumers keep the picker inside the `+`
	// popup.
	const agentSelectorLayout = isStreamingUxExperienceM1EnabledNoExposure() ? 'inline' : undefined;

	// The selector sits inside the editor row rather than the trailing action
	// cluster, so ai-mate hides it for `frameless` consumers unless they opt in.
	// Post Stream Review opts in (CCI-17298) to reach parity with the standard
	// prompt. Unlike the trailing actions — which PSR reveals only once the user
	// starts composing — the selector stays visible so the active agent is always
	// discoverable.
	const showAgentSelectorWhenFrameless = isPostStreamReview;

	const { cloudId, orgId, userId } = useMemo(
		() => editorPluginAIProvider?.getChannelVariables?.('rovo-agent') || backupChannelVariables,
		[editorPluginAIProvider],
	);
	const product = editorPluginAIProvider.product;
	const productKey = providerProductToProductKey(product);
	const isPageEditor =
		appearance === 'full-page' || appearance === 'full-width' || appearance === 'max';

	const { width } = useSharedPluginStateWithSelector(api, ['width'], (states) => {
		return {
			width: states.widthState?.width,
		};
	});

	const nextSessionId = useSharedPluginStateWithSelector(
		api,
		['aiExperience'],
		({ aiExperienceState }) => aiExperienceState?.aiDecorationExperiencePluginState.nextSessionId,
	);
	const pendingInlineRovoStreamingTargetId = useSharedPluginStateWithSelector(
		api,
		['aiExperience'],
		({ aiExperienceState }) =>
			aiExperienceState?.aiDecorationExperiencePluginState.pendingInlineRovoStreamingTargetId,
	);
	// Keep the admitted token stable so late callbacks cannot settle a replacement attempt.
	const inlineRovoEditAttemptIdRef = useRef<string | undefined>(undefined);
	const getInlineRovoEditAttemptId = useCallback(() => inlineRovoEditAttemptIdRef.current, []);
	const startInlineRovoEditHandoffPresentation = useCallback(() => {
		const attemptId = inlineRovoEditAttemptIdRef.current;
		if (attemptId) {
			api?.aiExperience?.actions.startInlineRovoEditHandoffPresentation?.(attemptId);
		}
	}, [api]);
	// Suppress prompt-close abort after an explicit Rovo Chat handoff.
	const admittedInlineRovoEditHandedOffRef = useRef(false);
	// Distinguish synchronous Chat auto-close from later user cancellation.
	const admittedInlineRovoEditPendingAutoCloseRef = useRef(false);

	const existingShowLabsLozenge = useMemo(() => {
		if (productKey === 'confluence') {
			if (!fg('aifc_onboarding_ga_enabled')) {
				return true;
			}

			// eslint-disable-next-line @atlaskit/platform/use-recommended-utils
			if (!FeatureGates.getExperimentValue('cc-aifc-databases-experiment', 'isEnabled', false)) {
				return true;
			}
		}

		return false;
	}, [productKey]);

	const selectionContext = useSharedPluginStateWithSelector(
		api,
		['aiExperience'],
		({ aiExperienceState }) => aiExperienceState?.selectionContext ?? null,
	);

	const selectionLabsLozengeVariant = useMemo(
		() => getSelectionLabsLozengeVariant(selectionContext?.selectionFragment ?? null),
		[selectionContext],
	);

	const isMauiOrMediaSelection =
		expValEquals('cc-maui-experiment', 'isEnabled', true) &&
		selectionLabsLozengeVariant !== undefined;

	const source = isMauiOrMediaSelection
		? 'CONFLUENCE_MAUI_INLINE_EDITOR'
		: productKey === 'confluence' && isPageEditor
			? 'CONFLUENCE_INLINE_EDITOR'
			: 'INLINE_EDITOR';
	const isMauiInlineRemixEditPrompt =
		isMauiOrMediaSelection &&
		!isPostStreamReview &&
		!openInRovoChatConfig &&
		rovoInlineChatAppearance === undefined;
	// Keep ai-mate mounted for completion cleanup, but hide the edit prompt while
	// InlineEditReadyBar owns the post-stream UI.
	const shouldHidePromptAfterComplete =
		isMauiInlineRemixEditPrompt && isExperimentEnabled('cc_maui_polish_changes_batch_6');
	const shouldOpenChatOnError =
		(source === 'CONFLUENCE_INLINE_EDITOR' || source === 'CONFLUENCE_MAUI_INLINE_EDITOR') &&
		fg('cc-maui-phase-2') &&
		expValEquals('cc-maui-experiment', 'isEnabled', true);

	// The Post Stream Review prompt (frameless) hides the LABS lozenge — the
	// design has no badge in that surface — without affecting any other inline
	// chat usage.
	const showLabsLozenge =
		!isPostStreamReview &&
		((existingShowLabsLozenge && selectionLabsLozengeVariant === undefined) ||
			isMauiOrMediaSelection);

	useEffect(() => {
		setCancelRovoPrompt?.(undefined);
		return () => {
			setIsRovoLoading(false);
		};
	}, [setCancelRovoPrompt, setIsRovoLoading]);

	const teardownInlineSession = useCallback(
		({ streamEnded }: { streamEnded: boolean }) => {
			cancel({ shouldClearSelectionContext: streamEnded });
			setIsRovoLoading(false);
			if (
				// eslint-disable-next-line @atlaskit/platform/no-preconditioning -- gate + experiment layering for MAUI rollout (cc-maui-phase-2 + cc-maui-experiment); will simplify post-rollout
				editorView &&
				fg('cc-maui-phase-2') &&
				expValEqualsNoExposure('cc-maui-experiment', 'isEnabled', true)
			) {
				createEndInlineRovoStreamingCommand()(editorView.state, editorView.dispatch);
			}
			// [CCI-17658] Skip reset during per-segment rework — the segment state
			// (streamReviewSegments, aiContentPositions, reworkSnapshot) must survive
			// the Rovo streaming infrastructure's abort/restart cycle.
			if (editorView) {
				const sessionState = getAIExperienceContextPluginState(editorView.state);
				if (!sessionState?.reworkSnapshot) {
					api?.core?.actions?.execute(resetSessionState());
				}
			} else {
				api?.core?.actions?.execute(resetSessionState());
			}
		},
		[cancel, setIsRovoLoading, api?.core?.actions, editorView],
	);

	const handleAbort = useCallback(
		({ streamEnded }: { streamEnded: boolean }) => {
			const attemptId = inlineRovoEditAttemptIdRef.current;
			const handedOff = admittedInlineRovoEditHandedOffRef.current;
			const pendingAutoClose = admittedInlineRovoEditPendingAutoCloseRef.current;
			if (handedOff) {
				return;
			}
			if (pendingAutoClose) {
				admittedInlineRovoEditPendingAutoCloseRef.current = false;
				// Rovo Chat now owns terminal settlement.
				cancel({ shouldClearSelectionContext: false });
				return;
			}
			if (attemptId) {
				api?.aiExperience?.actions.reportInlineRovoEdit?.(attemptId, 'generation-aborted');
			}
			teardownInlineSession({ streamEnded });
			if (isMauiOrMediaSelection) {
				remixEditExistingExperience.abort({
					metadata: { reason: 'user_cancel' },
				});
				void getBrowserBridgeAPI()?.send(AifcExperienceTrackingCommand, {
					status: 'abort',
					reason: 'user_cancel',
					experienceName: ExperienceName.REMIX_REGENERATE,
					senderId: ROVO_EXPERIENCE_TRACKING_SENDER_CLIENT_ID,
					receiverId: ROVO_CHAT_CLIENT_ID,
				});
			}
		},
		[api, cancel, teardownInlineSession, isMauiOrMediaSelection],
	);

	/**
	 * Error handoff (openChatOnError): same UI teardown as abort, but do not abort
	 * remix experiences. aiMateRemixRegenerate fails via send-message collect;
	 * remixEditExistingExperience is failed here because this path previously only aborted it.
	 */
	const handleError = useCallback(
		({ streamEnded }: { streamEnded: boolean }) => {
			const attemptId = inlineRovoEditAttemptIdRef.current;
			if (attemptId) {
				api?.aiExperience?.actions.reportInlineRovoEdit?.(attemptId, 'generation-error');
			}
			teardownInlineSession({ streamEnded });
			if (isMauiOrMediaSelection) {
				remixEditExistingExperience.failure({
					metadata: { reason: 'stream_error_open_chat' },
				});
			}
		},
		[api, teardownInlineSession, isMauiOrMediaSelection],
	);

	const handleBeforeSubmit = useCallback(
		(prompt: DocNode) => {
			admittedInlineRovoEditHandedOffRef.current = false;
			admittedInlineRovoEditPendingAutoCloseRef.current = false;
			inlineRovoEditAttemptIdRef.current = undefined;
			const isMediaSelection = selectionContext?.selectionFragment?.some(
				(node: { type?: string }) => node.type === 'mediaSingle' || node.type === 'media',
			);
			const stagedTargetId =
				pendingInlineRovoStreamingTargetId ?? selectionContext?.localIds?.[0] ?? undefined;
			if (isMauiOrMediaSelection && stagedTargetId) {
				const admission = api?.aiExperience?.actions.beginInlineRovoEdit?.(stagedTargetId);
				inlineRovoEditAttemptIdRef.current =
					admission?.status === 'admitted' ? admission.attemptId : undefined;
				if (admission?.status === 'rejected') {
					// Reject before downstream request side effects.
					api?.aiExperience?.actions.endInlineRovoStreaming?.();
					cancel({ shouldClearSelectionContext: false });
					return true;
				}
			}
			const onBeforeSubmitForRovoChat = openInRovoChatConfig?.onBeforeSubmit;
			if (onBeforeSubmitForRovoChat) {
				// Claim the synchronous prompt close before invoking the handoff.
				admittedInlineRovoEditHandedOffRef.current = Boolean(inlineRovoEditAttemptIdRef.current);
				try {
					const handled = onBeforeSubmitForRovoChat(prompt);
					if (handled) {
						startInlineRovoEditHandoffPresentation();
						return true;
					}
				} catch (error) {
					admittedInlineRovoEditHandedOffRef.current = false;
					const attemptId = inlineRovoEditAttemptIdRef.current;
					if (attemptId) {
						api?.aiExperience?.actions.reportInlineRovoEdit?.(attemptId, 'generation-aborted');
					}
					throw error;
				}
				admittedInlineRovoEditHandedOffRef.current = false;
			}

			if (isMauiOrMediaSelection && !fg('cc-maui-disable-edit-experience-tracker-fixes')) {
				// remixType is 'infographic' for media nodes; for MAUI apps it's
				// only known from the backend response (added via updateAttributes
				// in useEditorRovoBridgeCommandListener when the stream settles).
				const featureArea = getRemixFeatureArea({
					remixType: isMediaSelection ? 'infographic' : undefined,
				});
				void getBrowserBridgeAPI()?.send(AifcExperienceTrackingCommand, {
					status: 'start',
					experienceName: ExperienceName.REMIX_REGENERATE,
					actionId: selectionContext?.localIds?.[0],
					attributes: {
						regenerateSource: 'inline-existing-node',
						editSource: 'rovoBar',
						selectionLocalId: selectionContext?.localIds?.[0],
						...(featureArea ? { featureArea } : {}),
					},
					...(isMediaSelection && { remixType: 'infographic' }),
					senderId: ROVO_EXPERIENCE_TRACKING_SENDER_CLIENT_ID,
					receiverId: ROVO_CHAT_CLIENT_ID,
				});
			}

			const pendingAutoCloseAttemptId = inlineRovoEditAttemptIdRef.current;
			admittedInlineRovoEditPendingAutoCloseRef.current = Boolean(pendingAutoCloseAttemptId);
			// Expire the synchronous auto-close suppression if neither callback consumes it.
			queueMicrotask(() => {
				if (inlineRovoEditAttemptIdRef.current === pendingAutoCloseAttemptId) {
					admittedInlineRovoEditPendingAutoCloseRef.current = false;
				}
			});
			return false;
		},
		[
			api?.aiExperience?.actions,
			cancel,
			isMauiOrMediaSelection,
			openInRovoChatConfig,
			pendingInlineRovoStreamingTargetId,
			selectionContext,
			startInlineRovoEditHandoffPresentation,
		],
	);

	const onSubmit = useCallback(
		(cancel: () => void) => {
			admittedInlineRovoEditPendingAutoCloseRef.current = false;
			// Promote the staged target id (set by the toolbar handler) — this is
			// what triggers the consumer-visible loading overlay. No-op when
			// nothing is staged (e.g. modal opened via a non-toolbar path).
			// `expValEqualsNoExposure` because the toolbar click is the designated
			// `cc-maui-experiment` exposure point.
			if (
				// eslint-disable-next-line @atlaskit/platform/no-preconditioning -- gate + experiment layering for MAUI rollout (cc-maui-phase-2 + cc-maui-experiment); will simplify post-rollout
				editorView &&
				fg('cc-maui-phase-2') &&
				expValEqualsNoExposure('cc-maui-experiment', 'isEnabled', true)
			) {
				createCommitInlineRovoStreamingCommand()(editorView.state, editorView.dispatch);
			}

			// Start remixRegenerate when editing an embedded MAUI/media node
			if (isMauiOrMediaSelection) {
				remixEditExistingExperience.start();
				remixEditExistingExperience.mark(REMIX_EDIT_EXISTING_MARKS.SUBMIT);
				if (fg('cc-maui-disable-edit-experience-tracker-fixes')) {
					// remixType is 'infographic' for media nodes; for MAUI apps it's
					// only known from the backend response (added via updateAttributes
					// in useEditorRovoBridgeCommandListener when the stream settles).
					const isMediaSelection = selectionContext?.selectionFragment?.some(
						(node: { type?: string }) => node.type === 'mediaSingle' || node.type === 'media',
					);
					const featureArea = getRemixFeatureArea({
						remixType: isMediaSelection ? 'infographic' : undefined,
					});
					void getBrowserBridgeAPI()?.send(AifcExperienceTrackingCommand, {
						status: 'start',
						experienceName: ExperienceName.REMIX_REGENERATE,
						actionId: selectionContext?.localIds?.[0],
						attributes: featureArea ? { featureArea } : {},
						...(isMediaSelection && { remixType: 'infographic' }),
						senderId: ROVO_EXPERIENCE_TRACKING_SENDER_CLIENT_ID,
						receiverId: ROVO_CHAT_CLIENT_ID,
					});
				}
			}

			editorPluginAIProvider.onSubmit?.();
			setIsRovoLoading(true);
			// Submitting is what puts the freegen thinking bar on screen, so this is the
			// canonical exposure point for the collapse experiment. Post-stream review never
			// shows a collapsible bar, so those users are not enrolled.
			if (!isPostStreamReview) {
				isCollapseLoadingBarEnabled();
			}
			// Fire prompt event for AIFC. We don't have access to the request id as it's not generated
			// at this point of time and nothing else is available at this stage.
			// For now this will just give us an indication of prompt volume.
			api?.analytics?.actions.fireAnalyticsEvent<UserFlowAEP, 'customEventType'>({
				action: 'prompt',
				actionSubject: 'editorPluginAI',
				actionSubjectId: 'userFlow',
				attributes: {
					aiGroupId: nextSessionId,
					experienceName: CONFIG_ITEM_KEYS.FREE_GENERATE_AIFC,
					actionTaken: '',
					editCount: 0,
					aiSessionId: '',
					inlineStreamingAvailable: true,
				},
				eventType: EVENT_TYPE.UI,
			});

			const cancelRovoPrompt = () => {
				cancel();
			};
			// give DOM time to update the screen
			setTimeout(() => {
				setCancelRovoPrompt?.(() => cancelRovoPrompt);
			}, 100);
		},
		[
			api,
			nextSessionId,
			editorPluginAIProvider,
			setCancelRovoPrompt,
			setIsRovoLoading,
			editorView,
			isMauiOrMediaSelection,
			isPostStreamReview,
			selectionContext?.localIds,
			selectionContext?.selectionFragment,
		],
	);

	const shouldShowCompact = useMemo(() => {
		if (!width) {
			return false;
		} else if (fg('platform_maui_jira_ui_fix')) {
			return width <= Breakpoint.Small * 2;
		}

		return width <= Breakpoint.Small;
	}, [width]);

	const firePromptInputEngaged = useRef(
		debounce((payload: PromptContentChangePayload, fireEvent: typeof api) => {
			fireEvent?.analytics?.actions.fireAnalyticsEvent<UserFlowAEP, 'customEventType'>({
				action: 'promptInputUpdated',
				actionSubject: 'editorPluginAI',
				actionSubjectId: 'userFlow',
				attributes: {
					aiGroupId: nextSessionId,
					experienceName: CONFIG_ITEM_KEYS.FREE_GENERATE_AIFC,
					actionTaken: '',
					editCount: 0,
					aiSessionId: '',
					inlineStreamingAvailable: true,
					characterCount: payload.textLength,
					hasMentionOrInlineCard: payload.hasMentionOrInlineCard,
				},
				eventType: EVENT_TYPE.UI,
			});
		}, 500),
	).current;

	useEffect(() => {
		return () => {
			firePromptInputEngaged.cancel();
		};
	}, [firePromptInputEngaged]);

	const isSmartlinkToolbarInlinePromptAnalyticsEnabled = expValEqualsNoExposure(
		'platform_editor_rovobutton_smartlink_toolbar_exp',
		'isEnabled',
		true,
	);

	const onPromptContentChange = useCallback(
		(payload: PromptContentChangePayload) => {
			// Always forward the raw payload to the parent surface so it can track
			// prompt emptiness (e.g. PSR "press Enter to accept").
			onPromptContentChangeProp?.(payload);
			if (payload.isEmpty && !payload.hasMentionOrInlineCard) {
				return;
			}
			// Only fire the prompt-engaged analytics when its experiment is on. The
			// parent forwarding above is wired independently (e.g. PSR "press Enter to
			// accept"), so gate this to avoid emitting `promptInputUpdated` in that context.
			if (isSmartlinkToolbarInlinePromptAnalyticsEnabled) {
				firePromptInputEngaged(payload, api);
			}
		},
		[
			api,
			firePromptInputEngaged,
			onPromptContentChangeProp,
			isSmartlinkToolbarInlinePromptAnalyticsEnabled,
		],
	);

	// Memoized key forces RovoInlineChatPrompt to remount when the skill selection changes
	const userADFInputKey = useMemo(
		() => (userADFInput ? JSON.stringify(userADFInput) : undefined),
		[userADFInput],
	);

	// If a session is active - we'll show the loading screen elsewhere and we can hide this
	if (nextSessionId) {
		return null;
	}

	return (
		<FlagsProvider>
			<RovoInlineChatPrompt
				key={userADFInputKey}
				autoClose={autoClose}
				source={source}
				productKey={productKey}
				siteId={cloudId}
				orgId={orgId}
				objectId={targetId}
				_showLabsLozengeVariant={selectionLabsLozengeVariant}
				showMauiMediaPlaceholder={isMauiOrMediaSelection}
				hideAfterComplete={shouldHidePromptAfterComplete}
				openChatOnError={shouldOpenChatOnError}
				creationContextParams={editorPluginAIProvider.creationContextParams}
				onAbort={handleAbort}
				onError={handleError}
				// eslint-disable-next-line @atlassian/perf-linting/no-unstable-inline-props -- Ignored via go/ees017 (to be fixed)
				onCancel={() => {
					/* noop - this is to allow the rovo inline chat to manage cancel behaviour.
					 * We shouldn't end the AI experience directly. */
				}}
				popupsMountPoint={popupsMountPoint}
				// The following nops usages are like this because we want to ensure that only the AI plugin or the Orchestrator
				// are controlling the xstate. We do not want this to end the ai experience directly. Instead the rovo bridge
				// will call the orchestrator which will then end the experience, on both complete and abort.
				// IMPORTANT!! Do not end the AI experience on the complete callback. This can cause problems because the
				// rovo bridge may still have events to stream in the queue, and it's possible that this could result in
				// the experience being ended immediately on mount, which would then unmount the prompt. This is because the rovo bridge
				// can have a final response in its queue which has never fluushed previously, because something else unmounted this
				// component before the final response was flushed.
				// eslint-disable-next-line @atlassian/perf-linting/no-unstable-inline-props -- Ignored via go/ees017 (to be fixed)
				onComplete={() => {
					setIsRovoLoading(false);
					if (
						// eslint-disable-next-line @atlaskit/platform/no-preconditioning -- gate + experiment layering for MAUI rollout (cc-maui-phase-2 + cc-maui-experiment); will simplify post-rollout
						editorView &&
						fg('cc-maui-phase-2') &&
						expValEqualsNoExposure('cc-maui-experiment', 'isEnabled', true)
					) {
						const inlineEditReadyTargetId =
							aiExperienceDecorationPluginKey.getState(editorView.state)
								?.inlineRovoStreamingTargetId ?? undefined;
						createEndInlineRovoStreamingCommand()(editorView.state, editorView.dispatch);
						// Keep the ready state separate from streaming completion, but carry the
						// edited node id across teardown so visibility and View target the result.
						if (isMauiOrMediaSelection) {
							createShowInlineEditReadyCommand(inlineEditReadyTargetId)(
								editorView.state,
								editorView.dispatch,
							);
						}
					}
				}}
				// When the progress bar killswitch is enabled, the inline chat's
				// onBeforeComplete will delay calling complete() until the progress bar
				// fill-to-100% animation finishes, preventing the UI from flashing the
				// prompt input before the parent unmounts this component.
				// eslint-disable-next-line @atlassian/perf-linting/no-unstable-inline-props -- Ignored via go/ees017 (to be fixed)
				onBeforeComplete={
					isMauiOrMediaSelection
						? (complete) => {
								complete();
							}
						: undefined
				}
				onSubmit={onSubmit}
				onBeforeSubmit={handleBeforeSubmit}
				onSubmitAutoClose={startInlineRovoEditHandoffPresentation}
				getInlineRovoEditAttemptId={getInlineRovoEditAttemptId}
				enableAgents={editorPluginAIProvider.isRovoEnabled}
				currentUserId={
					(expValEquals('platform_editor_agent_mentions', 'isEnabled', true) &&
						fg('platform_editor_agent_mentions_drop_one_fixes')) ||
					fg('platform_editor_agent_mention_ask_rovo_jira')
						? userId
						: undefined
				}
				_showLabsLozenge={showLabsLozenge}
				compact={shouldShowCompact}
				onPromptContentChange={
					isSmartlinkToolbarInlinePromptAnalyticsEnabled || onPromptContentChangeProp
						? onPromptContentChange
						: undefined
				}
				defaultValue={userADFInput}
				appearance={rovoInlineChatAppearance}
				showTrailingActions={showTrailingActions}
				thinkingBarScrollContainer={thinkingBarScrollContainer}
				shouldRenderInPortal={editorPluginAIProvider.shouldRenderInPortal}
				placeholderText={aifcPlaceholderText}
				agentSelectorLayout={agentSelectorLayout}
				showAgentSelectorWhenFrameless={showAgentSelectorWhenFrameless}
			/>
		</FlagsProvider>
	);
}
